import { capitalCase } from 'change-case';
import React, { useCallback } from 'react';
import { LFC } from '../../../../Legacy';
import { Component, ComponentType } from '../../../../concepts/objects';
import { Participant } from '../../../canvas/participants/Participant';
import { ExportDimension, ExportDimensionType, ExportDimensionUIProps, ExportValue } from '../ExportDimension';
import { Box, Input, Select } from './../../../../ui';

type Formatter = (ptpt: Participant) => string;
const FormatterItem = (id: string, label: string, impl: Formatter) => ({ id, label, apply: impl });
type FormatterItem = ReturnType<typeof FormatterItem>;

const formatters = [
    FormatterItem('id', 'Id', ptpt => ptpt?.id ?? ''),
    FormatterItem('name', 'Name', ptpt => ptpt?.name ?? ''),
    FormatterItem('email', 'Email', ptpt => ptpt?.email ?? ''),
    FormatterItem('both', 'Name & Email', ptpt => !!ptpt ? `${ptpt.name} <${ptpt.email}>` : ''),
];

const getFormatter = (id: string) => formatters.find(x => x.id === id) ?? null;
const defaultFormatter = getFormatter('name');

const selectAdapter = {
    key: (tm: FormatterItem) => tm.id,
    label: (tm: FormatterItem) => tm.label,
};

const ParticipantColumnEditor: LFC<ExportDimensionUIProps> = ({ item, onEdit }) => {

    const selected = getFormatter(item.params.style) ?? defaultFormatter;
    const onSelect = useCallback((tm: FormatterItem) => onEdit(ExportDimension.update(item, { style: tm.id })), [item, onEdit]);

    return <Box padding="1" width="24">
        <Input.Frame>
            <Input.Top>
                <Input.Label>Format:</Input.Label>
            </Input.Top>
            <Select items={formatters} selected={selected} adapter={selectAdapter} onChange={onSelect} />
        </Input.Frame>
    </Box>;
};

export const DefineParticipantColumn = <T extends Component>(
    type: ComponentType<T>, label: string, description: string, getter: (obj: T) => Participant) => {
    const desc = Component.describe(type);
    const id = `${desc.name}:${capitalCase(label)}`;
    return ExportDimensionType({
        id,
        label,
        description,
        ui: ParticipantColumnEditor,
        createInstance(params) {
            return ExportDimension({
                id,
                type: this,
                params: params ?? {},
                label,
                read(rows) {
                    const format = getFormatter(this.params.style) ?? defaultFormatter;
                    const head = [ExportValue(this.id, this.label)];
                    const body = rows.map(c => {
                        const ptpt = c.is(type) ? getter(c as T) : null;
                        return ExportValue(id, format.apply(ptpt));
                    });
                    return [head.concat(body)];
                }
            });
        }
    });
};
